import React, {
    ReactElement,
    PropsWithChildren,
    createContext,
    useContext,
    useMemo,
    DependencyList
} from 'react'
import {Container, ServiceIdentifier} from 'inversify'

const ContainerContext = createContext<Container | null>(null)

const ContainerProvider = ({
    value,
    children
}: PropsWithChildren<ContainerProviderProps>) => {
    return <ContainerContext.Provider value={value}>
        {children}
    </ContainerContext.Provider>
}

type ContainerProviderProps = {
    value: Container
}

const ContainerConsumer = ({
    children
}: ContainerConsumerProps) => {
    return <ContainerContext.Consumer>
        {(container) => {
            if (!container || !children) {
                return null
            }

            return children(container)
        }}
    </ContainerContext.Consumer>
}

type ContainerConsumerProps = {
    children?(container: Container): ReactElement
}

const useContainer = () => {
    const context = useContext(ContainerContext)

    if (!context) {
        throw new Error(`useContainer: Context is null`)
    }

    return context
}

const useService = <T,>(service: ServiceIdentifier<T>, deps: DependencyList = []): T => {
    const c = useContainer()

    // Dynamic dependency list is this hook's API (callers pass deps that force
    // re-resolution from the container), so it can't be an array literal here.
    // eslint-disable-next-line react-hooks/use-memo
    return useMemo(() => c.get(service), deps)
}

export {
    ContainerProvider,
    ContainerConsumer,
    useContainer,
    useService
}
